import {useEffect, useState} from "react";
import moment from "moment";
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  Select,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  CircularProgress,
  Tab,
  Tabs,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Divider,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import ListToolbar from "src/components/common/ListToolbar";
import MyPagination from "src/components/common/MyPagination";
import {asyncRequest} from "src/services/network";
import UserInfo from "src/components/common/UserInfo";

const API_TYPE_OPTIONS = [
  {value: "", label: "全部"},
  {value: "syncUserAccount", label: "账务同步"},
  {value: "syncUserInfo", label: "用户信息同步"},
  {value: "createUser", label: "创建用户"},
  {value: "robotConfig", label: "开通VIP"},
  {value: "getRobotConfig", label: "查询VIP过期时间"},
];

const LIST_TABLE_HEAD = [
  {id: "id", label: "ID", alignRight: false},
  {id: "apiType", label: "接口类型", alignRight: false},
  {id: "user", label: "用户", alignRight: false},
  {id: "orderId", label: "订单号", alignRight: false},
  {id: "summary", label: "同步摘要", alignRight: false},
  {id: "success", label: "状态", alignRight: false},
  {id: "duration", label: "耗时(ms)", alignRight: false},
  {id: "createdAt", label: "调用时间", alignRight: false},
  {id: "action", label: "操作", alignRight: false},
];

function parseSummary(apiType, requestParams) {
  if (apiType !== "syncUserAccount" || !requestParams) return null;
  try {
    const params = typeof requestParams === "string" ? JSON.parse(requestParams) : requestParams;
    const syncInfo = params.syncInfo || params;
    const goldenBeanNum = syncInfo.golden_bean_num ?? syncInfo.goldenBeanNum;
    if (goldenBeanNum !== undefined && goldenBeanNum !== null) {
      return `本次充值金豆 ${goldenBeanNum}`;
    }
  } catch (e) {
  }
  return null;
}

const STATS_TYPE_TABLE_HEAD = [
  {id: "apiType", label: "接口类型", alignRight: false},
  {id: "total", label: "总调用次数", alignRight: false},
  {id: "successCount", label: "成功次数", alignRight: false},
  {id: "failCount", label: "失败次数", alignRight: false},
  {id: "successRate", label: "成功率", alignRight: false},
  {id: "avgDuration", label: "平均耗时(ms)", alignRight: false},
];

export default function AuctionApiLog() {
  const [tabIndex, setTabIndex] = useState(0);

  const [listItems, setListItems] = useState(null);
  const [listTotal, setListTotal] = useState(0);
  const [listFilter, setListFilter] = useState({
    page: 0,
    size: 20,
    apiType: "",
    uid: "",
    orderId: "",
    success: "",
    startDate: "",
    endDate: "",
  });

  const [statsData, setStatsData] = useState(null);
  const [statsFilter, setStatsFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (tabIndex === 0) {
      loadList();
    }
  }, [listFilter]);

  useEffect(() => {
    if (tabIndex === 1) {
      loadStats();
    }
  }, [statsFilter]);

  async function loadList() {
    setListItems(null);
    const {data, count} = await asyncRequest({
      path: "/jingxi/auction-api-log/list",
      data: listFilter,
    });
    setListItems(data);
    setListTotal(count);
  }

  async function loadStats() {
    setStatsData(null);
    const {data} = await asyncRequest({
      path: "/jingxi/auction-api-log/stats",
      data: statsFilter,
    });
    setStatsData(data);
  }

  async function openDetail(id) {
    setDetailItem(null);
    setDetailLoading(true);
    setDetailDialogOpen(true);
    const {data} = await asyncRequest({
      path: "/jingxi/auction-api-log/detail",
      data: {id},
    });
    setDetailItem(data);
    setDetailLoading(false);
  }

  function handleTabChange(_, newVal) {
    setTabIndex(newVal);
    if (newVal === 0 && !listItems) loadList();
    if (newVal === 1 && !statsData) loadStats();
  }

  const isListNotFound = listItems && listItems.length === 0;

  return (
    <Page title="鲸禧拍卖API日志">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            鲸禧拍卖 API 调用日志
          </Typography>
        </Stack>

        <Card>
          <Box sx={{borderBottom: 1, borderColor: "divider", px: 3, pt: 1}}>
            <Tabs value={tabIndex} onChange={handleTabChange}>
              <Tab label="调用日志"/>
              <Tab label="统计概览"/>
            </Tabs>
          </Box>

          {tabIndex === 0 && (
            <>
              <ListToolbar
                showSearch={false}
                onReset={() =>
                  setListFilter({
                    page: 0,
                    size: 20,
                    apiType: "",
                    uid: "",
                    orderId: "",
                    success: "",
                    startDate: "",
                    endDate: "",
                  })
                }
                onRefresh={loadList}
              >
                <Grid container rowGap={2}>
                  <Grid item sm={6} md={3}>
                    <TextField
                      size="small"
                      style={{width: 200}}
                      label="开始日期"
                      placeholder="YYYY-MM-DD"
                      autoComplete="off"
                      value={listFilter.startDate}
                      onChange={(e) => setListFilter({...listFilter, startDate: e.target.value, page: 0})}
                    />
                  </Grid>
                  <Grid item sm={6} md={3}>
                    <TextField
                      size="small"
                      style={{width: 200}}
                      label="结束日期"
                      placeholder="YYYY-MM-DD"
                      autoComplete="off"
                      value={listFilter.endDate}
                      onChange={(e) => setListFilter({...listFilter, endDate: e.target.value, page: 0})}
                    />
                  </Grid>
                  <Grid item sm={6} md={3}>
                    <FormControl size="small">
                      <InputLabel>接口类型</InputLabel>
                      <Select
                        label="接口类型"
                        value={listFilter.apiType}
                        onChange={(e) => setListFilter({...listFilter, apiType: e.target.value, page: 0})}
                        style={{width: 180}}
                      >
                        {API_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item sm={6} md={3}>
                    <FormControl size="small">
                      <InputLabel>调用状态</InputLabel>
                      <Select
                        label="调用状态"
                        value={listFilter.success}
                        onChange={(e) => setListFilter({...listFilter, success: e.target.value, page: 0})}
                        style={{width: 150}}
                      >
                        <MenuItem value="">全部</MenuItem>
                        <MenuItem value={1}>成功</MenuItem>
                        <MenuItem value={0}>失败</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item sm={6} md={3}>
                    <TextField
                      size="small"
                      style={{width: 200}}
                      label="用户ID"
                      placeholder="用户ID"
                      autoComplete="off"
                      value={listFilter.uid}
                      onChange={(e) => setListFilter({...listFilter, uid: e.target.value, page: 0})}
                    />
                  </Grid>
                  <Grid item sm={6} md={3}>
                    <TextField
                      size="small"
                      style={{width: 200}}
                      label="订单号"
                      placeholder="订单号"
                      autoComplete="off"
                      value={listFilter.orderId}
                      onChange={(e) => setListFilter({...listFilter, orderId: e.target.value, page: 0})}
                    />
                  </Grid>
                </Grid>
              </ListToolbar>

              <Scrollbar>
                <TableContainer sx={{minWidth: 800}}>
                  <Table>
                    <ListHead headLabel={LIST_TABLE_HEAD}/>
                    {!listItems ? (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={8}>
                            <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                              <CircularProgress/>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <TableBody>
                        {listItems.map((row) => {
                          const {
                            id,
                            apiType,
                            apiTypeName,
                            user,
                            uid,
                            orderId,
                            success,
                            duration,
                            createdAt,
                            requestParams
                          } = row;
                          const summary = parseSummary(apiType, requestParams);
                          return (
                            <TableRow hover key={id} tabIndex={-1}>
                              <TableCell align="left">{id}</TableCell>
                              <TableCell align="left">
                                <Chip
                                  size="small"
                                  label={apiTypeName || apiType}
                                  color={
                                    apiType === "syncUserAccount"
                                      ? "primary"
                                      : apiType === "createUser"
                                        ? "secondary"
                                        : "default"
                                  }
                                />
                              </TableCell>
                              <TableCell align="left">
                                {user ? <UserInfo {...user} enablePreview/> : uid ? `UID: ${uid}` : "-"}
                              </TableCell>
                              <TableCell align="left">{orderId || "-"}</TableCell>
                              <TableCell align="left">
                                {summary ? (
                                  <Typography variant="body2" color="info.main">
                                    {summary}
                                  </Typography>
                                ) : ("-")}
                              </TableCell>
                              <TableCell align="left">
                                <Chip
                                  size="small"
                                  label={success ? "成功" : "失败"}
                                  color={success ? "success" : "error"}
                                />
                              </TableCell>
                              <TableCell align="left">{duration ?? "-"}</TableCell>
                              <TableCell align="left">
                                {moment(createdAt).format("YYYY-MM-DD HH:mm:ss")}
                              </TableCell>
                              <TableCell align="left">
                                <Typography
                                  variant="body2"
                                  sx={{cursor: "pointer", color: "primary.main", textDecoration: "underline"}}
                                  onClick={() => openDetail(id)}
                                >
                                  详情
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    )}
                    {isListNotFound && (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={8} sx={{py: 5}}>
                            <SearchNotFound/>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Scrollbar>
              <MyPagination
                total={listTotal}
                page={listFilter.page}
                size={listFilter.size}
                onChange={(_, v, newSize) => newSize !== undefined ? setListFilter({
                  ...listFilter,
                  page: 0,
                  size: newSize
                }) : setListFilter({...listFilter, page: v - 1})}
              />
            </>
          )}

          {tabIndex === 1 && (
            <>
              <ListToolbar
                showSearch={false}
                onReset={() => setStatsFilter({startDate: "", endDate: ""})}
                onRefresh={loadStats}
              >
                <Grid container rowGap={2}>
                  <Grid item sm={6} md={3}>
                    <TextField
                      size="small"
                      style={{width: 200}}
                      label="开始日期"
                      placeholder="YYYY-MM-DD"
                      autoComplete="off"
                      value={statsFilter.startDate}
                      onChange={(e) => setStatsFilter({...statsFilter, startDate: e.target.value})}
                    />
                  </Grid>
                  <Grid item sm={6} md={3}>
                    <TextField
                      size="small"
                      style={{width: 200}}
                      label="结束日期"
                      placeholder="YYYY-MM-DD"
                      autoComplete="off"
                      value={statsFilter.endDate}
                      onChange={(e) => setStatsFilter({...statsFilter, endDate: e.target.value})}
                    />
                  </Grid>
                </Grid>
              </ListToolbar>

              {!statsData ? (
                <Stack sx={{width: "100%", height: 200}} alignItems="center" justifyContent="center">
                  <CircularProgress/>
                </Stack>
              ) : (
                <Box sx={{p: 3}}>
                  <Typography variant="h6" gutterBottom>
                    总体概览
                  </Typography>
                  <Grid container spacing={2} mb={4}>
                    {[
                      {label: "总调用次数", value: statsData.total},
                      {label: "成功次数", value: statsData.successCount, color: "success.main"},
                      {label: "失败次数", value: statsData.failCount, color: "error.main"},
                      {label: "成功率", value: statsData.successRate},
                      {label: "平均耗时", value: `${statsData.avgDuration} ms`},
                    ].map((item) => (
                      <Grid key={item.label} item xs={6} sm={4} md={2}>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="h5" color={item.color || "text.primary"}>
                            {item.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{mb: 3}}/>

                  <Typography variant="h6" gutterBottom>
                    按接口类型统计
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <ListHead headLabel={STATS_TYPE_TABLE_HEAD}/>
                      <TableBody>
                        {(statsData.typeStats || []).map((stat) => (
                          <TableRow hover key={stat.apiType}>
                            <TableCell>
                              <Chip
                                size="small"
                                label={stat.apiTypeName || stat.apiType}
                                color={
                                  stat.apiType === "syncUserAccount"
                                    ? "primary"
                                    : stat.apiType === "createUser"
                                      ? "secondary"
                                      : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>{stat.total}</TableCell>
                            <TableCell>
                              <Typography color="success.main" variant="body2">
                                {stat.successCount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography color={stat.failCount > 0 ? "error.main" : "text.primary"} variant="body2">
                                {stat.failCount}
                              </Typography>
                            </TableCell>
                            <TableCell>{stat.successRate}</TableCell>
                            <TableCell>{stat.avgDuration} ms</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </>
          )}
        </Card>
      </Container>

      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>API 调用日志详情</DialogTitle>
        <DialogContent>
          {detailLoading || !detailItem ? (
            <Stack alignItems="center" justifyContent="center" sx={{height: 150}}>
              <CircularProgress/>
            </Stack>
          ) : (
            <Box sx={{pt: 1}}>
              <Grid container spacing={2}>
                {[
                  {label: "日志ID", value: detailItem.id},
                  {label: "接口类型", value: detailItem.apiTypeName || detailItem.apiType},
                  {label: "调用状态", value: detailItem.success ? "成功" : "失败"},
                  {label: "耗时", value: `${detailItem.duration ?? "-"} ms`},
                  {label: "用户UID", value: detailItem.uid || "-"},
                  {label: "订单号", value: detailItem.orderId || "-"},
                  {
                    label: "调用时间",
                    value: moment(detailItem.createdAt).format("YYYY-MM-DD HH:mm:ss"),
                  },
                ].map((item) => (
                  <Grid key={item.label} item xs={12} sm={6}>
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2" color="text.secondary" sx={{minWidth: 80}}>
                        {item.label}：
                      </Typography>
                      <Typography variant="body2">{item.value}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              {detailItem.requestBody && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    请求参数
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      background: "#f5f5f5",
                      p: 2,
                      borderRadius: 1,
                      fontSize: 12,
                      overflow: "auto",
                      maxHeight: 200,
                    }}
                  >
                    {JSON.stringify(
                      typeof detailItem.requestBody === "string"
                        ? JSON.parse(detailItem.requestBody)
                        : detailItem.requestBody,
                      null,
                      2
                    )}
                  </Box>
                </Box>
              )}

              {detailItem.responseBody && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    响应内容
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      background: "#f5f5f5",
                      p: 2,
                      borderRadius: 1,
                      fontSize: 12,
                      overflow: "auto",
                      maxHeight: 200,
                    }}
                  >
                    {JSON.stringify(
                      typeof detailItem.responseBody === "string"
                        ? JSON.parse(detailItem.responseBody)
                        : detailItem.responseBody,
                      null,
                      2
                    )}
                  </Box>
                </Box>
              )}

              {detailItem.errorMsg && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom color="error">
                    错误信息
                  </Typography>
                  <Box
                    sx={{
                      background: "#fff5f5",
                      border: "1px solid",
                      borderColor: "error.light",
                      p: 2,
                      borderRadius: 1,
                      fontSize: 12,
                    }}
                  >
                    {detailItem.errorMsg}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
